import { useState, useCallback, useRef, useEffect } from 'react';

interface UseUndoRedoOptions {
  maxHistorySize?: number;
}

interface UseUndoRedoReturn<T> {
  state: T;
  setState: (newState: T | ((prev: T) => T)) => void;
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  clearHistory: () => void;
}

/**
 * Custom hook for managing undo/redo functionality
 * @param initialState - Initial state value
 * @param options - Configuration options
 * @returns State management object with undo/redo capabilities
 */
export function useUndoRedo<T>(
  initialState: T,
  options: UseUndoRedoOptions = {}
): UseUndoRedoReturn<T> {
  const { maxHistorySize = 50 } = options;

  // History stack and current position
  const [history, setHistory] = useState<T[]>([initialState]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Current state
  const currentState = history[currentIndex];

  // Add state to history (immediate, no debouncing)
  const setState = useCallback((newState: T | ((prev: T) => T)) => {
    try {
      const resolvedState = typeof newState === 'function'
        ? (newState as (prev: T) => T)(currentState)
        : newState;

      // Validate state is not null/undefined
      if (resolvedState === null || resolvedState === undefined) {
        console.warn('useUndoRedo: Attempted to set null/undefined state, ignoring');
        return;
      }

      // Don't add duplicate states
      if (JSON.stringify(resolvedState) === JSON.stringify(currentState)) {
        return;
      }

      setHistory((prevHistory) => {
        // Remove any future states when making a new change
        const newHistory = prevHistory.slice(0, currentIndex + 1);
        newHistory.push(resolvedState);

        // Limit history size
        if (newHistory.length > maxHistorySize) {
          return newHistory.slice(-maxHistorySize);
        }

        return newHistory;
      });

      setCurrentIndex((prevIndex) => {
        const newIndex = prevIndex + 1;
        return newIndex >= maxHistorySize ? maxHistorySize - 1 : newIndex;
      });
    } catch (error) {
      console.error('useUndoRedo: Error in setState:', error);
    }
  }, [currentState, currentIndex, maxHistorySize]);

  // Undo function - simple and reliable
  const undo = useCallback(() => {
    try {
      if (currentIndex > 0 && history[currentIndex - 1] !== undefined) {
        setCurrentIndex(currentIndex - 1);
      }
    } catch (error) {
      console.error('useUndoRedo: Error in undo:', error);
    }
  }, [currentIndex, history]);

  // Redo function - simple and reliable
  const redo = useCallback(() => {
    try {
      if (currentIndex < history.length - 1 && history[currentIndex + 1] !== undefined) {
        setCurrentIndex(currentIndex + 1);
      }
    } catch (error) {
      console.error('useUndoRedo: Error in redo:', error);
    }
  }, [currentIndex, history]);

  // Clear history
  const clearHistory = useCallback(() => {
    setHistory([currentState]);
    setCurrentIndex(0);
  }, [currentState]);

  // Keyboard shortcuts - use capture phase to ensure they work even in modals
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if user is typing in an input/textarea (but allow in contenteditable)
      const target = e.target as HTMLElement;
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      const isContentEditable = target.isContentEditable;

      // Allow undo/redo in contenteditable, but not in regular inputs/textareas
      if (isInput && !isContentEditable) {
        return;
      }

      // Cmd/Ctrl + Z for undo
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        e.stopPropagation();
        undo();
      }
      // Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y for redo
      if (((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') ||
          ((e.metaKey || e.ctrlKey) && e.key === 'y')) {
        e.preventDefault();
        e.stopPropagation();
        redo();
      }
    };

    // Use capture phase to catch events before they reach modals
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [undo, redo]);

  return {
    state: currentState,
    setState,
    undo,
    redo,
    canUndo: currentIndex > 0,
    canRedo: currentIndex < history.length - 1,
    clearHistory,
  };
}
